import type { JobTypeInput } from '@chukua/common';
import { Button, Divider, Table } from 'antd';
import { Package, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { buildJobTypeColumns } from './build-job-type-columns';
import { JobTypeFormDrawer } from './JobTypeFormDrawer';
import type { JobType } from '../../../reference-data.api';
import { RequiredDocumentTypesPanel, type RequiredDocumentTypesPanelProps } from '../../required-document-types/components/RequiredDocumentTypesPanel';

interface JobTypesPanelProps {
  types: JobType[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: JobTypeInput) => Promise<boolean>;
  onUpdate: (id: string, input: JobTypeInput) => Promise<boolean>;
  onDeactivate: (row: JobType) => Promise<void>;
  onReactivate: (row: JobType) => Promise<void>;
  onResetSaveError: () => void;
  /** The shared required-document vocabulary's own management surface — forwarded as one bundle rather than nine more props. */
  requiredDocumentTypePanel: RequiredDocumentTypesPanelProps;
}

/** Client-side paging (Ant's default) — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables). */
export function JobTypesPanel({
  types,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
  requiredDocumentTypePanel,
}: JobTypesPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<JobType | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: JobType) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildJobTypeColumns({ onEdit: openEdit, onDeactivate, onReactivate });
  const activeRequiredDocumentTypes = requiredDocumentTypePanel.types.filter((d) => d.isActive);
  const requiredDocumentTypeOptions = editing
    ? [...activeRequiredDocumentTypes, ...editing.requiredDocuments.filter((required) => !activeRequiredDocumentTypes.some((d) => d.code === required.code))]
    : activeRequiredDocumentTypes;
  const newButton = (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={openCreate}>
      New job type
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex justify-end">{newButton}</div>
        {loading && <TableSkeleton label="Loading job types" />}
        {!loading && types.length === 0 && (
          <EmptyState icon={Package} title="No job types yet" description="Add the job types this deployment dispatches.">
            {newButton}
          </EmptyState>
        )}
        {!loading && types.length > 0 && <Table rowKey="id" dataSource={types} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />}
      </div>
      <Divider className="my-0" />
      <RequiredDocumentTypesPanel {...requiredDocumentTypePanel} />
      <JobTypeFormDrawer
        open={drawerOpen}
        editing={editing}
        requiredDocumentTypes={requiredDocumentTypeOptions}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
